/* ============================================================
   ANIMATIONS.JS — Scroll-triggered animations
   Pure IntersectionObserver, zero dependencies
   ============================================================ */

'use strict';

// ─── Fade-in / slide-up observer ──────────────────────────────────────────────

function initScrollAnimations() {
  const elements = document.querySelectorAll('[data-animate]');
  if (!elements.length) return;

  // Respect reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    elements.forEach(el => el.classList.add('animated'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
          observer.unobserve(entry.target); // fire once
        }
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  elements.forEach(el => observer.observe(el));
}


// ─── Stagger children ─────────────────────────────────────────────────────────

function initStaggerAnimations() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-stagger]').forEach(container => {
    const children = container.querySelectorAll('[data-stagger-item]');
    if (!children.length) return;

    if (prefersReducedMotion) {
      children.forEach(el => el.classList.add('animated'));
      return;
    }

    children.forEach((child, i) => {
      child.style.transitionDelay = `${i * 80}ms`;
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            children.forEach(child => child.classList.add('animated'));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );

    observer.observe(container);
  });
}


// ─── Parallax (lightweight, header image only) ────────────────────────────────

function initParallax() {
  const el = document.querySelector('[data-parallax]');
  if (!el) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  const strength = parseFloat(el.dataset.parallax) || 0.3;

  let ticking = false;

  window.addEventListener('scroll', () => {
    if (ticking) return;
    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      el.style.transform = `translateY(${scrollY * strength}px)`;
      ticking = false;
    });
    ticking = true;
  }, { passive: true });
}


// ─── Count-up numbers ─────────────────────────────────────────────────────────

function initCountUp() {
  const elements = document.querySelectorAll('[data-count-up]');
  if (!elements.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);

        const el      = entry.target;
        const target  = parseInt(el.dataset.countUp);
        const suffix  = el.dataset.suffix || '';
        const prefix  = el.dataset.prefix || '';
        const duration = 1400;

        if (prefersReducedMotion) {
          el.textContent = prefix + target.toLocaleString('nl-NL') + suffix;
          return;
        }

        const start     = Date.now();
        const startVal  = parseInt(el.textContent) || 0;

        function step() {
          const progress = Math.min((Date.now() - start) / duration, 1);
          // ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.floor(startVal + (target - startVal) * eased);
          el.textContent = prefix + current.toLocaleString('nl-NL') + suffix;
          if (progress < 1) requestAnimationFrame(step);
        }

        requestAnimationFrame(step);
      });
    },
    { threshold: 0.5 }
  );

  elements.forEach(el => observer.observe(el));
}


// ─── Image reveal (clip-path wipe) ────────────────────────────────────────────

function initImageReveal() {
  const elements = document.querySelectorAll('[data-reveal]');
  if (!elements.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  elements.forEach(el => {
    el.style.clipPath = 'inset(0 100% 0 0)';
    el.style.transition = 'clip-path .9s cubic-bezier(.77,0,.175,1)';
  });

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.clipPath = 'inset(0 0% 0 0)';
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  elements.forEach(el => observer.observe(el));
}


// ─── Hover-tilt on product cards (subtle 3D) ─────────────────────────────────

function initTiltCards() {
  // Only on desktop + non-touch
  if (window.matchMedia('(hover: none)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('[data-tilt]').forEach(card => {
    const STRENGTH = 6;

    card.addEventListener('mousemove', e => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width  - 0.5; // -0.5 to 0.5
      const y = (e.clientY - rect.top)  / rect.height - 0.5;

      card.style.transform = `
        perspective(600px)
        rotateY(${x * STRENGTH}deg)
        rotateX(${-y * STRENGTH}deg)
        scale(1.02)
      `;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = '';
      card.style.transition = 'transform .4s ease';
    });

    card.addEventListener('mouseenter', () => {
      card.style.transition = 'transform .1s ease';
    });
  });
}


// ─── Lazy background images ───────────────────────────────────────────────────

function initLazyBgs() {
  document.querySelectorAll('[data-bg]').forEach(el => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            el.style.backgroundImage = `url("${el.dataset.bg}")`;
            observer.unobserve(el);
          }
        });
      },
      { rootMargin: '200px 0px' }
    );
    observer.observe(el);
  });
}


// ─── Boot ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initStaggerAnimations();
  initParallax();
  initCountUp();
  initImageReveal();
  initTiltCards();
  initLazyBgs();
});
